// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.HeatmapChartOptionsBuilder.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import DefaultChartOptionsBuilder from './Izenda.DefaultChartOptionsBuilder';
import HighchartUtils from 'share/components/charting/highcharts/Izenda.HighchartUtils';
import ReportPartUtils from 'share/utils/Izenda.ReportPartUtils';
import { exportClass } from 'share/Izenda.ServiceProvider';
import { head, get, forEach, size, toString, isNil, uniq, merge, isString, groupBy, first, map } from 'lodash/fp';

import ChartDefaultConfig from 'logic/data/Izenda.ChartDefaultConfig';
import ProcessDataUtils from 'share/utils/Izenda.ProcessDataUtils';

const getValueFromMappingField = ProcessDataUtils.getValueFromMappingField;
const X_AXIS_CONTAINER_NAME = 'labels';
const Y_AXIS_CONTAINER_NAME = 'values';
const VALUE_LABELS = 'valuesLabels';

@exportClass('HeatmapChartOptionsBuilder')
export default class HeatmapChartOptionsBuilder extends DefaultChartOptionsBuilder {
  constructor(...baseArgs) {
    super(...baseArgs);
  }

  /**
   * Build Highchart variable options for each chart type
   * @param {string} visualType Highchart chart type
   * @param {Object} highchartOptions Highchart options
   * @param {Object} chartData Chart data
   */
  buildOptionsByType(visualType: string, userOptions, dataParser): Object {
    const dataStructure = dataParser.dataStructure || {};
    const izendaSeriesConfig = userOptions.izendaSeriesConfig || {};

    const xAxisField = head(dataStructure[X_AXIS_CONTAINER_NAME]);
    if (izendaSeriesConfig[xAxisField.fieldNameAlias].fieldDataType === 'Datetime') {
      izendaSeriesConfig[xAxisField.fieldNameAlias].axisOption.izCategory = true;
    }

    const parsedOption =
      super.buildOptionsByType(
        visualType,
        merge(
          {
            X_AXIS_CONTAINER_NAME: X_AXIS_CONTAINER_NAME,
            Y_AXIS_CONTAINER_NAME: VALUE_LABELS,
            SEPARATOR_CONTAINER_NAME: Y_AXIS_CONTAINER_NAME
          },
          userOptions
        ),
        dataParser
      ) || {};

    const yAxisField = head(dataStructure[Y_AXIS_CONTAINER_NAME]);
    const valueField = head(dataStructure[VALUE_LABELS]);

    const series = parseData(xAxisField, yAxisField, valueField);

    const COLOR_AXIS = {
      MIN_COLOR: ChartDefaultConfig.DefaultConfig.HeatMap.MIN_COLOR,
      MAX_COLOR: isNil(userOptions.colors) ? this.DefaultColors[0] : userOptions.colors[0]
    };

    let chartOptions = {
      // only support one series
      series: [
        {
          data: series.data,
          borderWidth: 1,

          xAxisField: xAxisField,
          yAxisField: yAxisField,
          valueField: valueField,

          dataLabels: {
            color: '#000000',
            userHTML: true,
            formatter: getDataLabels
          }
        }
      ],

      xAxis: {
        categories:  map(String, series.xAxisCat),
        axisName: getFieldName(xAxisField),
        tickPositioner: HighchartUtils.getCategoriesTicksPositioner()
      },

      yAxis: {
        categories:  map(String, series.yAxisCat),
        axisName: getFieldName(yAxisField),
        tickPositioner: HighchartUtils.getCategoriesTicksPositioner()
      },

      plotOptions: {
        series: {
          borderWidth: 1
        }
      },

      colorAxis: {
        minColor: COLOR_AXIS.MIN_COLOR,
        maxColor: COLOR_AXIS.MAX_COLOR
      },
      legend: {
        layout: 'vertical',
        margin: 0
      }
    };

    chartOptions.xAxis = $izenda.extend(true, parsedOption.xAxis, chartOptions.xAxis);
    chartOptions.yAxis = $izenda.extend(true, parsedOption.yAxis, chartOptions.yAxis);

    // add sub-report to y-axis
    HighchartUtils.addPointActionLink(
      false,
      userOptions,
      chartOptions.yAxis.labels,
      chartOptions.yAxis.axisName,
      get(chartOptions.yAxis.axisName, izendaSeriesConfig)
    );
    return chartOptions;

    function getDataLabels() {
      const formattedValue = HighchartUtils.getFormattedDataByPoint('value', this.point, true);
      return get('data', formattedValue);
    }

    function getFieldName(field: any) {
      return get('fieldNameAlias', field);
    }

    function groupData(groupInfo: string | any, groupNames: Array<any>) {
      const source = dataParser.records;
      const names = [];

      // groupInfo is container-name: group by first-field of container
      groupInfo = isString(groupInfo) ? first(dataStructure[groupInfo]) : groupInfo;
      const groupedData = groupInfo ? groupData(groupInfo, source) : source;
      forEach(name => {
        groupNames.push(name);
      }, uniq(names));

      return groupedData;

      function groupData(groupInfo, source) {
        let groupName;
        return groupBy(record => {
          groupName = ReportPartUtils.getOriginalValue(groupInfo, record);
          groupName = ReportPartUtils.getAlternativeText(groupName, izendaSeriesConfig[groupInfo.fieldNameAlias], groupInfo) || groupName;
          names && names.push(groupName);
          return groupName;
        }, source);
      }
    }

    function parseData(xAxisField: any, yAxisField: any, valueField: any) {
      const yAxisSource = getYCat(yAxisField);
      const xAxisCat = [];
      const yAxisCat = yAxisSource.arr;
      const yAxisCatObj = yAxisSource.obj;
      const serieData = [];
      const groupedData = groupData(xAxisField, xAxisCat);
      let source;
      let column, y, val;

      forEach(groupName => {
        source = groupedData[groupName];
        column = [];
        serieData.push(column);

        source.forEach(record => {
          y = toString(ReportPartUtils.getOriginalValue(yAxisField, record));
          val = ReportPartUtils.getOriginalValue(valueField, record);
          column[yAxisCatObj[y]] = {
            value: val,
            record: record
          };
        });
      }, xAxisCat);

      const data = [];
      const numRows = size(yAxisCat);
      let rowIdx = 0;

      while (rowIdx < numRows) {
        xAxisCat.forEach((column, colInx) => {
          val = serieData[colInx][rowIdx] || {};
          val.x = colInx;
          val.y = rowIdx;
          isNil(val.value) && (val.value = 0);

          data.push(val);
        });
        rowIdx++;
      }

      return {
        xAxisCat: xAxisCat,
        yAxisCat: yAxisCat,
        data: data
      };

      function getYCat(yAxisField: any) {
        let values = ProcessDataUtils.getListValueByField(yAxisField, dataParser.records, izendaSeriesConfig[yAxisField.fieldNameAlias]);
        const obj = {};
        values = uniq(values);

        values.forEach((val, idx) => {
          obj[toString(val)] = idx;
        });

        return {
          arr: values,
          obj: obj
        };
      }
    }
  }
}



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/ReportBody/ReportPartType/ReportPartChart/Izenda.HeatmapChartOptionsBuilder.js